import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import LoadingSpinner from '../../../../landing/components/LoadingSpinner';
import { MdAutorenew } from 'react-icons/md';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  PieController
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import './subscription_analytics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  PieController,
  Title,
  Tooltip,
  Legend
);

const SubscriptionAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeSubscriptions: 0,
    activeUsers: 0,
    multiSubUsers: 0,
    expiredSubscriptions: {
      count: 0,
      users: [],
      byProduct: {
        step1_library: 0,
        step2_library: 0,
        step1_mentorship: 0,
        step2_mentorship: 0,
        OET: 0,
        US_VISA_Counselling: 0
      }
    },
    productStats: {
      step1_library: 0,
      step2_library: 0,
      step1_mentorship: 0,
      step2_mentorship: 0
    },
    endingSoon: {
      count: 0,
      users: []
    },
    monthlyData: {
      labels: [],
      datasets: []
    }
  });

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        padding: 12,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };

  const pieChartData = {
    labels: ['Step 1 Library', 'Step 2 Library', 'Step 1 Mentorship', 'Step 2 Mentorship'],
    datasets: [
      {
        data: [
          stats.productStats.step1_library,
          stats.productStats.step2_library,
          stats.productStats.step1_mentorship,
          stats.productStats.step2_mentorship
        ],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };

  const expiredPieChartData = {
    labels: ['Step 1 Library', 'Step 2 Library', 'Step 1 Mentorship', 'Step 2 Mentorship', 'OET', 'US VISA Counselling'],
    datasets: [
      {
        data: [
          stats.expiredSubscriptions.byProduct.step1_library,
          stats.expiredSubscriptions.byProduct.step2_library,
          stats.expiredSubscriptions.byProduct.step1_mentorship,
          stats.expiredSubscriptions.byProduct.step2_mentorship,
          stats.expiredSubscriptions.byProduct.OET,
          stats.expiredSubscriptions.byProduct.US_VISA_Counselling
        ],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };

  const distributionData = {
    labels: ['Step 1 Library', 'Step 2 Library', 'Step 1 Mentorship', 'Step 2 Mentorship'],
    datasets: [
      {
        label: 'Active Subscriptions',
        data: [
          stats.productStats.step1_library,
          stats.productStats.step2_library,
          stats.productStats.step1_mentorship,
          stats.productStats.step2_mentorship
        ],
        backgroundColor: 'rgba(52, 152, 219, 0.7)',
        borderColor: 'rgba(52, 152, 219, 1)',
        borderWidth: 1,
        borderRadius: 6
      },
      {
        label: 'Expired Subscriptions',
        data: [
          stats.expiredSubscriptions.byProduct.step1_library,
          stats.expiredSubscriptions.byProduct.step2_library,
          stats.expiredSubscriptions.byProduct.step1_mentorship,
          stats.expiredSubscriptions.byProduct.step2_mentorship
        ],
        backgroundColor: 'rgba(231, 76, 60, 0.7)',
        borderColor: 'rgba(231, 76, 60, 1)',
        borderWidth: 1,
        borderRadius: 6
      }
    ]
  };

  useEffect(() => {
    fetchSubscriptionStats();
  }, []);

  const fetchSubscriptionStats = async () => {
    try {
      const startTime = performance.now();
      const subscriptionsRef = collection(db, 'subscriptions');
      const subscriptionsSnapshot = await getDocs(subscriptionsRef);
      
      console.log('Subscription Analytics - Fetching stats:', {
        operation: 'fetch_subscriptions',
        subscriptionsCount: subscriptionsSnapshot.size,
        timestamp: new Date().toISOString(),
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });
      
      let activeCount = 0;
      let activeUsers = new Set();
      let userSubscriptionCounts = new Map();
      let productStats = {
        step1_library: 0,
        step2_library: 0,
        step1_mentorship: 0,
        step2_mentorship: 0
      };
      let endingSoonUsers = [];
      
      let expiredCount = 0;
      let expiredUsers = [];
      let expiredByProduct = {
        step1_library: 0,
        step2_library: 0,
        step1_mentorship: 0,
        step2_mentorship: 0,
        OET: 0,
        US_VISA_Counselling: 0
      };
      
      const now = new Date();
      const twoWeeksFromNow = new Date();
      twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      subscriptionsSnapshot.forEach(doc => {
        const data = doc.data();
        const subscriptions = data.subscriptions || {};
        let userActiveSubscriptions = 0;

        Object.entries(subscriptions).forEach(([type, sub]) => {
          if (sub.status === 'active') {
            activeCount++;
            userActiveSubscriptions++;
            productStats[type]++;

            const endDate = new Date(sub.endDate);
            if (endDate <= twoWeeksFromNow && endDate > now) {
              endingSoonUsers.push({
                name: data.email,
                product: type,
                endDate: sub.endDate,
                uid: doc.id
              });
            }
          }
          
          if (sub.status === 'expired' || (sub.endDate && new Date(sub.endDate) < now && sub.status === 'active')) {
            expiredCount++;
            
            if (expiredByProduct.hasOwnProperty(type)) {
              expiredByProduct[type]++;
            }
            
            const endDate = new Date(sub.endDate);
            if (endDate >= thirtyDaysAgo && endDate <= now) {
              expiredUsers.push({
                name: data.email,
                product: type,
                endDate: sub.endDate,
                uid: doc.id,
                expiryDate: endDate.toISOString().split('T')[0],
                daysSinceExpiry: Math.floor((now - endDate) / (1000 * 60 * 60 * 24))
              });
            }
          }
        });

        if (userActiveSubscriptions > 0) {
          activeUsers.add(doc.id);
          userSubscriptionCounts.set(doc.id, userActiveSubscriptions);
        }
      });

      const multiSubUsersCount = Array.from(userSubscriptionCounts.values())
        .filter(count => count > 1).length;
        
      expiredUsers.sort((a, b) => new Date(b.endDate) - new Date(a.endDate));

      setStats(prevStats => ({
        ...prevStats,
        activeSubscriptions: activeCount,
        activeUsers: activeUsers.size,
        multiSubUsers: multiSubUsersCount,
        productStats,
        endingSoon: {
          count: endingSoonUsers.length,
          users: endingSoonUsers
        },
        expiredSubscriptions: {
          count: expiredCount,
          users: expiredUsers,
          byProduct: expiredByProduct
        }
      }));

      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="subc_altc_page">
      <SharedHeader />
      
      <main className="subc_altc_content">
        <div className="subc_altc_header">
          <button 
            className="subc_altc_back_btn"
            onClick={() => navigate('/admin/analytics')}
          >
            <i className="fas fa-arrow-left"></i> Back to Analytics
          </button>
          <h1>Subscription Analytics</h1>
        </div>

        <div className="subc_altc_dashboard">
          <div className="subc_altc_summary_cards">
            <div className="subc_altc_card">
              <h3>Active Subscriptions</h3>
              <p className="subc_altc_number">{stats.activeSubscriptions}</p>
            </div>
            <div className="subc_altc_card">
              <h3>Active Users (1+ Subscription)</h3>
              <p className="subc_altc_number">{stats.activeUsers}</p>
            </div>
            <div className="subc_altc_card">
              <h3>Users with Multiple Subscriptions</h3>
              <p className="subc_altc_number">{stats.multiSubUsers}</p>
            </div>
            <div className="subc_altc_card">
              <h3>Ending Soon (2 Weeks)</h3>
              <p className="subc_altc_number">{stats.endingSoon.count}</p>
            </div>
            <div className="subc_altc_card subc_altc_card_expired">
              <h3>Expired Subscriptions</h3>
              <p className="subc_altc_number">{stats.expiredSubscriptions.count}</p>
              <p className="subc_altc_subtitle">total expired</p>
            </div>
          </div>

          <div className="subc_altc_charts">
            <div className="subc_altc_chart_container">
              <h3>Subscription Distribution</h3>
              <div className="subc_altc_chart_wrapper">
                <Bar options={chartOptions} data={distributionData} />
              </div>
            </div>
            <div className="subc_altc_chart_container">
              <h3>Subscription Types</h3>
              <div className="subc_altc_chart_wrapper">
                <Pie options={chartOptions} data={pieChartData} />
              </div>
            </div>
          </div>

          <div className="subc_altc_detailed_stats">
            <h3>Subscriptions Ending in 2 Weeks</h3>
            <div className="subc_altc_table">
              {stats.endingSoon.users.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Product</th>
                      <th>End Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.endingSoon.users.map((user, index) => (
                      <tr key={index}>
                        <td>{user.name}</td>
                        <td>{user.product}</td>
                        <td>{formatDate(user.endDate)}</td>
                        <td>
                          <button className="subc_altc_action_btn">View Details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="subc_altc_no_data">No subscriptions ending soon</p>
              )}
            </div>
          </div>
          
          <div className="subc_altc_expired_section">
            <div className="subc_altc_section_header">
              <h2>Expired Subscriptions Analysis</h2>
              <p className="subc_altc_section_description">
                <MdAutorenew className="subc_altc_description_icon" />
                Track recently expired subscriptions and identify renewal opportunities
              </p>
            </div>
            
            <div className="subc_altc_charts">
              <div className="subc_altc_chart_container">
                <h3>Expired Subscriptions by Product</h3>
                <div className="subc_altc_chart_wrapper">
                  <Pie options={chartOptions} data={expiredPieChartData} />
                </div>
              </div>
            </div>
            
            <div className="subc_altc_detailed_stats">
              <h3>Recently Expired Subscriptions (Last 30 Days)</h3>
              <div className="subc_altc_table">
                {stats.expiredSubscriptions.users.length > 0 ? (
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Product</th>
                        <th>Expiry Date</th>
                        <th>Days Since Expiry</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.expiredSubscriptions.users.map((user, index) => (
                        <tr key={index}>
                          <td>{user.name}</td>
                          <td>{user.product}</td>
                          <td>{formatDate(user.endDate)}</td>
                          <td>{user.daysSinceExpiry}</td>
                          <td>
                            <div className="subc_altc_action_buttons">
                              <button className="subc_altc_action_btn subc_altc_renew_btn">
                                Renew
                              </button>
                              <button className="subc_altc_action_btn">
                                Contact
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="subc_altc_no_data">No recently expired subscriptions</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default SubscriptionAnalytics; 